import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Tooltip from '@mui/material/Tooltip';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { ArrowRightLeft, ChevronLeft, ChevronRight } from 'lucide-react';

import Chip from 'src/components/chip/Chip';
import { Flex } from 'src/components/flex';
import { LeadsIcon, PropertiesIcon, UsersIcon } from 'src/icons';
import { useLeadLabels, useProperties, usePropertyLabels } from 'src/services/api';
import {
    getAssigneesFilterLabel,
    getIngestionSourcesFilterLabel,
    getLeadStagesFilterLabel,
    getLeadLabelsFilterLabel,
    getExcludeLeadLabelsFilterLabel,
    getPropertyFilterLabel,
    getPropertyLabelsFilterLabel,
    getLastMessageDirectionFilterLabel,
} from 'src/pages/landlord/conversations/utils/filter-tabs';
import { countActiveFilterDimensions } from 'src/pages/landlord/conversations/utils/filter-narrowing';
import { type TConversationsFilter } from 'src/services/api/messages/use-conversations-search';
import { type IOtherUser, type IUser } from 'src/api/users-api';
import styles from 'src/pages/landlord/conversations/components/header/ConversationsInboxFilterGroup.module.css';

type Props = {
    teamMembers: Array<IUser | IOtherUser>;
    inboxFilters: TConversationsFilter | undefined;
};

type InboxFilterChip = {
    key: string;
    label: string;
    icon: React.ReactElement;
};

const chipsContainerVariants = {
    expanded: { transition: { staggerChildren: 0.05 } },
    collapsed: { transition: { staggerChildren: 0.03, staggerDirection: -1 } },
};

const chipVariants = {
    expanded: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 500, damping: 24 } },
    collapsed: { opacity: 0, scale: 0.8, transition: { duration: 0.1 } },
} as const;

/**
 * Read-only, collapsible group showing the current inbox's saved filters next to the inbox
 * title. Renders as an "INBOX FILTER N" chip that expands to reveal the individual filter
 * chips. Inbox filters can only be changed by editing the inbox on the Inboxes page, so the
 * chips here have no delete affordance.
 */
export default function ConversationsInboxFilterGroup({ teamMembers, inboxFilters }: Props) {
    const [isHovered, setIsHovered] = useState(false);
    const [isPinned, setIsPinned] = useState(false);

    const isHoverSupported = useMediaQuery('(hover: hover)');
    const isExpanded = (isHoverSupported && isHovered) || isPinned;

    const handleToggleClick = () => {
        if (isExpanded) {
            setIsPinned(false);
            setIsHovered(false);
        } else {
            setIsPinned(true);
        }
    };
    const { data: leadLabels } = useLeadLabels();
    const { data: properties } = useProperties();
    const { data: propertyLabels } = usePropertyLabels();

    const chips = useMemo<InboxFilterChip[]>(() => {
        if (!inboxFilters) {
            return [];
        }
        const result: InboxFilterChip[] = [];

        const assignees = getAssigneesFilterLabel(inboxFilters.assigneeIds, teamMembers);
        if (assignees) {
            result.push({ key: 'assignees', label: assignees, icon: <UsersIcon /> });
        }
        const ingestion = getIngestionSourcesFilterLabel(inboxFilters.ingestionSources);
        if (ingestion) {
            result.push({ key: 'ingestion', label: ingestion, icon: <DownloadRoundedIcon /> });
        }
        const leadStages = getLeadStagesFilterLabel(inboxFilters.leadStages);
        if (leadStages) {
            result.push({ key: 'leadStages', label: leadStages, icon: <LeadsIcon /> });
        }
        const leadLabelsLabel = getLeadLabelsFilterLabel(inboxFilters.leadLabelIds, leadLabels);
        if (leadLabelsLabel) {
            result.push({ key: 'leadLabels', label: leadLabelsLabel, icon: <LeadsIcon /> });
        }
        const excludeLeadLabels = getExcludeLeadLabelsFilterLabel(inboxFilters.excludeLeadLabelIds, leadLabels);
        if (excludeLeadLabels) {
            result.push({ key: 'excludeLeadLabels', label: excludeLeadLabels, icon: <LeadsIcon /> });
        }
        const property = getPropertyFilterLabel(inboxFilters.propertyIds, properties);
        if (property) {
            result.push({ key: 'property', label: property, icon: <PropertiesIcon /> });
        }
        if (inboxFilters.hasNoProperty) {
            result.push({ key: 'hasNoProperty', label: 'Lead has no property', icon: <PropertiesIcon /> });
        }
        const propertyLabelsLabel = getPropertyLabelsFilterLabel(inboxFilters.propertyLabelIds, propertyLabels);
        if (propertyLabelsLabel) {
            result.push({ key: 'propertyLabels', label: propertyLabelsLabel, icon: <PropertiesIcon /> });
        }
        const direction = getLastMessageDirectionFilterLabel(inboxFilters.lastMessageDirection);
        if (direction) {
            result.push({ key: 'direction', label: direction, icon: <ArrowRightLeft size={16} /> });
        }

        return result;
    }, [inboxFilters, teamMembers, leadLabels, properties, propertyLabels]);

    const count = countActiveFilterDimensions(inboxFilters);
    if (count === 0) {
        return null;
    }

    const ChevronIcon = isExpanded ? ChevronLeft : ChevronRight;

    return (
        <Flex
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            className={styles.inboxFilterGroup}
        >
            <Flex
                alignItems="center"
                gap={0.5}
                className={styles.inboxFilterToggle}
                onClick={handleToggleClick}
                role="button"
                aria-expanded={isExpanded}
                data-testid="inbox-filter-group-toggle"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <Typography variant="caption" className={styles.inboxFilterLabel}>
                    INBOX FILTER
                </Typography>
                <span className={styles.inboxFilterCount}>{count}</span>
                <ChevronIcon size={14} className={styles.inboxFilterChevron} />
            </Flex>

            <AnimatePresence initial={false}>
                {isExpanded && (
                    <motion.div
                        className={styles.inboxFilterCollapse}
                        initial={{ width: 0 }}
                        animate={{ width: 'auto' }}
                        exit={{ width: 0 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    >
                        <motion.div
                            className={styles.inboxFilterChips}
                            variants={chipsContainerVariants}
                            initial="collapsed"
                            animate="expanded"
                            exit="collapsed"
                        >
                            {chips.map(chip => (
                                <Tooltip key={chip.key} title={chip.label}>
                                    <motion.div
                                        className={styles.inboxFilterChipWrapper}
                                        variants={chipVariants}
                                    >
                                        <Chip
                                            className={styles.inboxFilterChip}
                                            variant="outlined"
                                            label={chip.label}
                                            icon={chip.icon}
                                        />
                                    </motion.div>
                                </Tooltip>
                            ))}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </Flex>
    );
}
